import {Button, Flex, FormControl, FormErrorMessage, FormHelperText, Input} from '@chakra-ui/react';
import {useDisclosure} from '@chakra-ui/react';
import {IconBlock} from 'components/layouts/aside-layout';
import {colors, useCustomerContext} from 'components/providers';
import {useCartContext} from 'components/providers';
import {useOrderContext} from 'components/providers';
import {AlertDialog} from 'components/shared/alert-dialog/AlertDialog';
import {useFormik} from 'formik';
import {useToast, useTranslation, useVoucher} from 'hooks';
import {FC, useEffect} from 'react';
import {promoValidationSchema} from 'utils/dataValidations';

export const Vouchers: FC = () => {
  const {t} = useTranslation();
  const toast = useToast();
  const {customer} = useCustomerContext();
  const {cartItemsPrice, resetDeliveryPrice} = useCartContext();
  const {setVoucher} = useOrderContext();
  const {validatePromoCode, resetData, data} = useVoucher();

  const {isOpen, onOpen, onClose} = useDisclosure();

  const {values, errors, handleSubmit, handleChange, resetForm} = useFormik({
    initialValues: {promoCode: ''},
    validationSchema: promoValidationSchema(t),
    onSubmit: ({promoCode}) => {
      if (!customer.phonenumber) toast.error(t('promoCodeRequired'));
      else validatePromoCode(promoCode);
    },
  });

  const isFreeDeliveryEligible =
    (customer.free_delivery && customer?.minimal_order_sum <= cartItemsPrice) ||
    (customer.month_order_count >= customer.each_n_free - 1 &&
      (customer?.minimal_order_sum <= cartItemsPrice || customer?.minimal_order_sum == 0));

  useEffect(() => {
    if (data?.is_valid && isFreeDeliveryEligible) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.is_valid]);

  const handleRejectPromo = () => {
    setVoucher(undefined);
    resetData();
    resetForm();
    onClose();
  };

  const formHasError = !!errors['promoCode'] || (data && !data.is_valid);

  return (
    <IconBlock heading={t('VOUCHER_TITLE')} icon="/icons/coupons.svg" pb={12}>
      <form onSubmit={handleSubmit}>
        <FormControl isInvalid={formHasError}>
          <Flex>
            <Input
              name="promoCode"
              onChange={(e) => {
                if (data) {
                  resetData();
                }
                handleChange(e);
              }}
              placeholder={t('ADD_VOUCHER_PLACEHOLDER')}
              height="auto"
              value={values.promoCode}
            />
            <Button
              variant="primary"
              ml={4}
              type="submit"
              _active={{backgroundColor: colors.inputBgGray1}}
              isDisabled={!values.promoCode.trim()}
            >
              {t('APPLY')}
            </Button>
          </Flex>
          <FormErrorMessage>{errors['promoCode'] || data?.message}</FormErrorMessage>
          {data?.is_valid && <FormHelperText color={colors.green}>{data?.message}</FormHelperText>}
        </FormControl>
      </form>

      <AlertDialog
        isOpen={isOpen}
        onClose={handleRejectPromo}
        onConfirm={() => {
          resetDeliveryPrice();
          onClose();
        }}
        title={t('FREE_DELIVERY_PROMO_CONFLICT_TITLE')}
        successButtonValue={t('APPLY')}
        cancelButtonValue={t('CANCEL')}
        reverseButtons
        closeOnConfirm={false}
      >
        {t('FREE_DELIVERY_PROMO_CONFLICT_TEXT')}
      </AlertDialog>
    </IconBlock>
  );
};
